import React, { useState, useEffect } from 'react';
import { propOr, path, isEmpty } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import {
  AppProvider,
  Button,
  Card,
  ButtonGroup,
  Form,
  FormLayout,
  Frame,
  Layout,
  Badge,
  Page,
  RangeSlider,
  Select,
  TextField,
  Toast,
  Modal,
  TextContainer,
  Heading,
} from '@shopify/polaris';
import SuccessBanner from '../components/SuccessBanner';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { CREATE_SHOP_CAREER_PORTAL, UPDATE_SHOP_CAREER_PORTAL } from '../apollo/mutation';
import { FONT_WEIGHT_OPTIONS, FONT_FAMILY_OPTIONS, FONT_CASING_OPTIONS } from '../../utils/formDefaultValues';
import ColorPicker from '../components/ColorPicker';

import { defaultWhiteColor, defaultGrayColor, defaultDarkGrayColor, hslaColor } from '../../utils/defaultColors';
import { keysToCamel } from '../../utils/normalizer';

const DiversityStatementSection = ({ history, shopifyToken, shopifyDomain, careerPageLink }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const shop = propOr({}, 'shop')(subscriptionData);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;
  const isSubscribedUser = !isEmpty(currentSubscription);
  const isPremiumUser = isSubscribedUser && currentSubscription.subscriptionType === 'Premium';

  const [form, setForm] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isSaveBarActive, setIsSaveBarActive] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const togglePreviewModal = () => setPreview(!preview);
  const toggleConfirmModal = () => setConfirmModal(!confirmModal);
  const [careerPortalTime, setCareerPortalTime] = useState();

  useEffect(() => {
    const careerPortal = propOr({}, 'careerPortal')(data);
    const deiStatmentBody = propOr('', 'deiStatmentBody')(careerPortal);
    const styleControls = propOr({}, 'styleControls')(careerPortal);
    const deiSection = propOr({}, 'dei_section')(styleControls);
    const styleControlsFormatted = keysToCamel(careerPortal.styleControls);
    const talentCommunitySection = propOr(false, 'talentCommunitySection')(careerPortal);
    const deiStatment = propOr(false, 'deiStatment')(careerPortal);
    setCareerPortalTime(careerPortal.pageTimestamps);
    setForm((prevForm) => ({
      ...prevForm,
      deiStatmentBody,
      ...styleControlsFormatted,
      deiStatment,
      deiSection: {
        ...prevForm.deiSection,
        backgroundColor: deiSection?.background_color || defaultWhiteColor,
        bodyColor: deiSection?.body_color || defaultGrayColor,
        headingColor: deiSection?.heading_color || defaultDarkGrayColor,
        headingWeight: deiSection?.heading_weight || '700',
        headingSize: deiSection?.heading_size || '26',
        headingFontFamily: deiSection?.heading_font_family || 'inherit',
        headingTextCasing: deiSection?.heading_text_casing || 'uppercase',
        bodyWeight: deiSection?.body_weight || '200',
        bodySize: deiSection?.body_size || '16',
        bodyFontFamily: deiSection?.body_font_family || 'inherit',
        topSpacing: deiSection?.top_spacing || '80',
        bottomSpacing: deiSection?.bottom_spacing || '80',
      },
      talentCommunityStyles: {
        ...styleControlsFormatted?.talentCommunitySection,
      },
      talentCommunitySection,
    }));
  }, [data]);

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const filterPageTimeLabel = (label) => {
    if (!careerPortalTime) return;
    return careerPortalTime.filter((val) => val.attribute == label)[0].label;
  };

  const updateField = (fieldName) => (value) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      [fieldName]: value,
    });
  };

  const updateColor = (fieldName) => (color) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      deiSection: {
        ...form.deiSection,
        [fieldName]: color,
      },
    });
  };

  const updateFontValue =
    ({ sectionName, fieldName }) =>
    (value) => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        [sectionName]: {
          ...form[sectionName],
          [fieldName]: `${value}`,
        },
      });
    };

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    refetch();
    setIsSaveBarActive(false);
    setShowSuccessBanner(true);
    setIsLoading(false);
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const onSubmit =
    (publish = true) =>
    (e) => {
      e?.preventDefault();
      setPreview(false);
      setConfirmModal(false);
      setIsLoading(true);
      const careerPortal = propOr({}, 'careerPortal')(data);
      const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
      const variables = {
        ...careerPortal,
        ...form,
        shopifyToken,
        deiStatment: publish,
      };
      delete variables.styleControls;

      save({ variables }).then(onSuccess).catch(onError);
    };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  return (
    <AppProvider
      i18n={{
        Polaris: {
          DropZone: {
            actionTitle: 'Add image',
          },
        },
      }}>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'Career Portal', onAction: () => { changeRoute('/career-portal-index'); } }]}
          title='Diversity statement section'
          subtitle={filterPageTimeLabel('dei_statment')}
          titleMetadata={
            <Badge status={form.deiStatment ? 'success' : 'default'}>{form.deiStatment ? 'Published' : 'Unpublished'}</Badge>
          }
          pagination={{
            hasPrevious: !isSaveBarActive,
            hasNext: !isSaveBarActive,
            nextURL: !isSaveBarActive ? `/career-portal/departments-section` : null,
            previousURL: !isSaveBarActive ? `/career-portal/employee-testimonials-section` : null,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <ButtonGroup>
              <Button plain monochrome onClick={togglePreviewModal}>
                Preview
              </Button>
              <Button
                primary
                onClick={onSubmit(true)}
                disabled={!isSaveBarActive && form.deiStatment}
                connectedDisclosure={
                  form.deiStatment
                    ? {
                        accessibilityLabel: 'Change section status',
                        actions: [{ content: 'Unpublish', onAction: toggleConfirmModal }],
                      }
                    : undefined
                }>
                Publish
              </Button>
            </ButtonGroup>
          }>
          <Form onSubmit={onSubmit()}>
            {showSuccessBanner && (
              <SuccessBanner
                history={history}
                title='Diversity statement section updated'
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={'View on your career portal'}
                detailsBody={'go to all career portal sections'}
                portalUrl={careerPageLink}
                detailsUrl={'/career-portal-index'}
              />
            )}
            <Layout>
              <Layout.AnnotatedSection
                title='Content'
                description={
                  <>
                    <p style={{ marginBottom: '10px' }}>
                      Communicate how your brand promotes a diverse work environment and team culture.
                    </p>
                  </>
                }>
                <Card sectioned>
                  <FormLayout>
                    <TextField
                      id='deiStatmentBody'
                      label='Paragraph text'
                      value={form.deiStatmentBody}
                      name='deiStatmentBody'
                      multiline={5}
                      onChange={updateField('deiStatmentBody')}
                    />
                  </FormLayout>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Layout'
                description='Customize the content alignment and spacing to match your store’s theme.'>
                <Card sectioned>
                  <FormLayout>
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Top spacing'
                        value={form.deiSection?.topSpacing}
                        onChange={updateFontValue({ sectionName: 'deiSection', fieldName: 'topSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.deiSection?.topSpacing}px`}
                      />
                      <RangeSlider
                        label='Bottom spacing'
                        value={form.deiSection?.bottomSpacing}
                        onChange={updateFontValue({ sectionName: 'deiSection', fieldName: 'bottomSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.deiSection?.bottomSpacing}px`}
                      />
                    </div>
                  </FormLayout>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Appearance'
                description='Customize the font and section background colors to match your store’s theme.'>
                <Card sectioned>
                  <Card.Section title='SECTION'>
                    <ColorPicker
                      colorName='Background color'
                      defaultColor={form.deiSection?.backgroundColor}
                      onSelectColor={updateColor('backgroundColor')}
                    />
                  </Card.Section>
                  <Card.Section title='TITLE TEXT'>
                    <ColorPicker
                      colorName='Title color'
                      defaultColor={form.deiSection?.headingColor}
                      onSelectColor={updateColor('headingColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Title font size'
                        value={form.deiSection?.headingSize}
                        onChange={updateFontValue({ sectionName: 'deiSection', fieldName: 'headingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.deiSection?.headingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'deiSection', fieldName: 'headingFontFamily' })}
                        value={form.deiSection?.headingFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'deiSection', fieldName: 'headingWeight' })}
                        value={form.deiSection?.headingWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'deiSection', fieldName: 'headingTextCasing' })}
                        value={form.deiSection?.headingTextCasing}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='STATEMENT TEXT'>
                    <ColorPicker
                      colorName='Statement color'
                      defaultColor={form.deiSection?.bodyColor}
                      onSelectColor={updateColor('bodyColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Statement font size'
                        value={form.deiSection?.bodySize}
                        onChange={updateFontValue({ sectionName: 'deiSection', fieldName: 'bodySize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.deiSection?.bodySize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Statement font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'deiSection', fieldName: 'bodyFontFamily' })}
                        value={form.deiSection?.bodyFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Statement font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'deiSection', fieldName: 'bodyWeight' })}
                        value={form.deiSection?.bodyWeight}
                      />
                    </div>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
            </Layout>
            <div className='career-portal__form-buttons'>
              <Button onClick={() => changeRoute('/career-portal-index')}>Cancel</Button>
              <Button primary submit disabled={!isSaveBarActive && form.deiStatment}>
                Publish
              </Button>
            </div>
          </Form>
          <Modal
            open={!!preview}
            large
            onClose={togglePreviewModal}
            primaryAction={{
              content: 'Publish',
              disabled: !isSaveBarActive && form.deiStatment,
              onAction: onSubmit(),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: togglePreviewModal,
              },
            ]}
            title={
              <TextContainer>
                <Heading>Preview diversity statement section</Heading>
                <p style={{ fontSize: '0.85em' }}>
                  Your theme fonts are not applied in the preview bellow, publish the section to your career portal to see how the
                  section will look on your store
                </p>
              </TextContainer>
            }
            loading={false}>
            <div className={'preview__modal'}>
              <section
                className='paragraph_section'
                style={{
                  backgroundColor: `${hslaColor(form.deiSection?.backgroundColor, '#fff')}`,
                }}>
                <div className='wrapper'>
                  <h2
                    className='heading_text'
                    style={{
                      color: `${hslaColor(form.deiSection?.headingColor, '#3a3a3a')}`,
                      fontSize: `${form.deiSection?.headingSize || '26'}px`,
                      fontWeight: `${form.deiSection?.headingWeight || '700'}`,
                    }}>
                    Diversity, Equity and Inclusion
                  </h2>
                  <p
                    className='body_content'
                    style={{
                      color: `${hslaColor(form.deiSection?.bodyColor, '#3a3a3a')}`,
                      fontSize: `${form.deiSection?.bodySize || '16'}px`,
                      fontWeight: `${form.deiSection?.bodyWeight || '200'}`,
                    }}>
                    {form.deiStatmentBody}
                  </p>
                </div>
              </section>
            </div>
          </Modal>
          <Modal
            open={confirmModal}
            onClose={toggleConfirmModal}
            title={`${form.deiStatment ? 'Unpublish' : 'Publish'} section`}
            primaryAction={{
              content: `Yes, ${form.deiStatment ? 'unpublish' : 'publish'} section`,
              onAction: onSubmit(!form.deiStatment),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: toggleConfirmModal,
              },
            ]}
            loading={false}>
            <Modal.Section>
              <p>{`Do you want to ${form.deiStatment ? 'unpublish' : 'publish'} this section to your career portal page?`}</p>
            </Modal.Section>
          </Modal>
        </Page>
        {toastMarkup}
      </Frame>
    </AppProvider>
  );
};

export default DiversityStatementSection;
